import isNil from 'lodash-es/isNil';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { useClient } from '../../../ClientProvider';
import { displayableStagesId } from '../../../resources/decitrait';
import useQuery from '../../../useQuery';
import { countDaysFromToday as countDaysFromTodayWithTz } from './utils';
const EMPTY_TIMELINE_ITEMS = [];
export const useDecitraitSummary = ({ plot, orgId }) => {
    const client = useClient();
    const decitraitPlotDetail = useQuery(client.decitrait.plotDetail({ plotId: plot.id }), {
        refetchOnWindowFocus: false
    }).data;
    const campaignSummaryRequest = useQuery(client.decitrait.summary({ plotId: plot.id }), {
        refetchOnWindowFocus: false
    });
    const allStages = useQuery(client.decitrait.getResources({ organizationId: orgId }), {
        refetchOnWindowFocus: false
    }).data?.stages;
    const campaignSummary = campaignSummaryRequest.data;
    const yesterday = moment().tz(plot.timezone).subtract(1, 'day').endOf('day');
    const summary = campaignSummary?.filter((elt) => elt.date.isAfter(yesterday));
    const stages = allStages?.filter((stage) => displayableStagesId.includes(stage.id));
    const todayState = summary?.[0];
    const todaysStage = todayState?.userSetStage || todayState?.computedStage;
    const todaysStageName = !isNil(todaysStage)
        ? stages?.find((stage) => stage.id === todaysStage)?.name || undefined
        : undefined;
    const countDaysFromToday = (futureDate, shouldRound) => countDaysFromTodayWithTz({
        date: futureDate ? { futureDate, timezone: plot.timezone } : undefined,
        shouldRound
    });
    const nextMildewThreat = countDaysFromToday(summary?.find((elt) => elt.mildew.protectionNeeded)?.date, false);
    const nextOidiumThreat = countDaysFromToday(summary?.find((elt) => elt.oidium.protectionNeeded)?.date, false);
    const findLastProtectedOrLastIfAllProtected = (isNotProtectedPredicate) => countDaysFromToday(
    // If evrey item is protected, return the last summary date
    summary?.every((e) => !isNotProtectedPredicate(e))
        ? summary[summary.length - 1]?.date
        : // Otherwise find the last date which is not protected
            summary?.find((elt) => isNotProtectedPredicate(elt))?.date);
    const protectionAgainstMildew = findLastProtectedOrLastIfAllProtected((elt) => !elt.mildew.protected);
    const protectionAgainstOidium = findLastProtectedOrLastIfAllProtected((elt) => !elt.oidium.protected);
    const protectionAgainstBlackrot = findLastProtectedOrLastIfAllProtected((elt) => elt.blackrotProtection === 0);
    const timelineItems = useMemo(() => stages?.map((stage, stageIdx) => {
        const stageDate = campaignSummary?.find(({ computedStage }) => computedStage === stage.id)?.date;
        const minStageDate = stageIdx !== 0
            ? campaignSummary
                ?.find(({ computedStage }) => computedStage === stages[stageIdx - 1].id)
                ?.date?.tz(plot.timezone)
                .clone()
                .add(1, 'day')
                .startOf('day')
                .toDate()
            : undefined;
        const maxStageDate = stageIdx !== stages.length - 1
            ? campaignSummary
                ?.find(({ computedStage }) => computedStage === stages[stageIdx + 1].id)
                ?.date?.tz(plot.timezone)
                .clone()
                .add(-1, 'day')
                .startOf('day')
                .toDate()
            : undefined;
        const isPassed = isNil(todaysStage) ? false : todaysStage >= stage.id;
        const result = {
            ...stage,
            key: stage.id,
            isPassed,
            maxDate: maxStageDate ?? moment().startOf('day').tz(plot.timezone).toDate(),
            minDate: minStageDate,
            currentDate: stageDate?.toDate(),
            isOverride: stage.id !== 1 &&
                !!campaignSummary?.find(({ userSetStage }) => userSetStage === stage.id)
        };
        return result;
    }) ?? EMPTY_TIMELINE_ITEMS, [stages, campaignSummary, plot.timezone, todaysStage]);
    return {
        campaignSummary,
        campaignSummaryRequest,
        stages,
        todaysStage,
        decitraitPlotDetail,
        todaysStageName,
        nextMildewThreat,
        nextOidiumThreat,
        protectionAgainstMildew,
        protectionAgainstOidium,
        protectionAgainstBlackrot,
        timelineItems
    };
};
